import { ApiProject } from "@incendium/api";
import { appendQueryParams } from "@incendium/inc-ts-helpers";
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import CopyClipboardBtn from "Components/CopyClipboardBtn/CopyClipboardBtn";
import { Glass } from "Components/Glass";
import { useFireflyScriptDomainOptions } from "features/project/hooks/useFireflyScriptDomainOptions";
import { useEffect, useMemo, useState } from "react";

const NoInitialLoadModeHelp = () => (
  <>
    <p>
      Turning no initial load on means that incendium js will not make the
      initial calls to track your pageview etc
    </p>
    <p>
      An example use case of this would be when using incendium on a SPA, where
      you want to manage the control of incendium via using a library or the inc
      function.
    </p>
  </>
);

const StyledPre = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  fontFamily: "monospace",
  fontSize: "12px",
  padding: "16px",
  borderRadius: "4px",
  border: `1px solid ${theme.palette.divider}`,
  whiteSpace: "pre-wrap",
}));

interface IProjectCodeSnippetprops {
  project: ApiProject;
}

function ProjectCodeSnippet({ project }: IProjectCodeSnippetprops) {
  const { scriptDomainOptions, defaultScriptBaseUrl } =
    useFireflyScriptDomainOptions();
  const [noInitialLoadMode, setNoInitialLoadMode] = useState(false);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [scriptType, setScriptType] = useState("async");
  const [selectedUrlSource, setSelectedUrlSource] = useState("default");
  const [hasChosenUrlSource, setHasChosenUrlSource] = useState(false);

  const selectedScriptBaseUrl = useMemo(() => {
    return (
      scriptDomainOptions.find((option) => option.id === selectedUrlSource)
        ?.baseUrl || defaultScriptBaseUrl
    );
  }, [selectedUrlSource, scriptDomainOptions, defaultScriptBaseUrl]);

  const isSelectedWwwDomain = useMemo(() => {
    return (
      scriptDomainOptions.find((option) => option.id === selectedUrlSource)
        ?.isWww || false
    );
  }, [selectedUrlSource, scriptDomainOptions]);

  useEffect(() => {
    setSelectedUrlSource("default");
    setHasChosenUrlSource(false);
  }, [project?.id]);

  useEffect(() => {
    if (hasChosenUrlSource) {
      return;
    }

    const firstSubdomainOption = scriptDomainOptions.find(
      (option) => option.id !== "default",
    );

    if (firstSubdomainOption) {
      setSelectedUrlSource(firstSubdomainOption.id);
    } else {
      setSelectedUrlSource("default");
    }
  }, [scriptDomainOptions, hasChosenUrlSource]);

  useEffect(() => {
    let url = `${selectedScriptBaseUrl}/${project?.key}/incendium.js`;

    if (noInitialLoadMode) {
      url = appendQueryParams(url, ["no_initial", "true"]);
    }

    setCode(
      `<script>\n\twindow.inc=window.inc||function(){\n\t\t(inc.args=inc.args||[]).push(arguments)\n\t};\n</script>\n<script ${scriptType && scriptType !== "none" ? `${scriptType} src="${url}"` : `src="${url}"`}></script>`,
    );
  }, [project, noInitialLoadMode, scriptType, selectedScriptBaseUrl]);

  return (
    <Glass elevation={1}>
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Stack spacing={1}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2">Code Snippet</Typography>
                <CopyClipboardBtn
                  copied={copied}
                  variant="outlined"
                  setCopied={setCopied}
                  text={code}
                  size="small"
                >
                  Copy
                </CopyClipboardBtn>
              </Box>
              <StyledPre>{code}</StyledPre>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Script Type"
                select
                value={scriptType}
                onChange={(e) => setScriptType(e.target.value)}
              >
                <MenuItem value="async">async</MenuItem>
                <MenuItem value="defer">defer</MenuItem>
                <MenuItem value="none">none</MenuItem>
              </TextField>
              {scriptDomainOptions.length > 1 && (
                <>
                  <TextField
                    size="small"
                    label="Script Domain"
                    select
                    value={selectedUrlSource}
                    onChange={(e) => {
                      setHasChosenUrlSource(true);
                      setSelectedUrlSource(e.target.value);
                    }}
                  >
                    {scriptDomainOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {isSelectedWwwDomain && (
                    <Alert severity="warning">
                      You selected a www domain. This is valid, but most setups
                      usually use the non-www root domain.
                    </Alert>
                  )}
                </>
              )}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={noInitialLoadMode}
                      onChange={() => setNoInitialLoadMode(!noInitialLoadMode)}
                    />
                  }
                  label="No Initial Load"
                  value={noInitialLoadMode}
                />
                <ClickableTooltip
                  placement="right"
                  text={<NoInitialLoadModeHelp />}
                  icon={<StyledHelp />}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        <br />
        <Divider />
      </Box>
    </Glass>
  );
}

export default ProjectCodeSnippet;
